/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: FB-000000 Remove this once entity, enittyType, trackingData are typed

import React, { useMemo, useCallback } from 'react';

import classnames from 'classnames';
import Overflow from 'rc-overflow';

import { TRANSFORMATIONS } from 'components/uiLibrary/Image';
import ImageAvatar from 'components/uiLibrary/ImageAvatar';
import ImageWithFallback from 'components/uiLibrary/ImageWithFallback';
import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';
import EntityName from 'components/Globals/EntityName';

import ImpressionTracker from 'utils/components/impressionTracker';
import { getOrganizationLocation } from 'utils/globals';
import usePageContext from 'utils/hooks/usePageContext';
import { getMediaSource } from 'utils/media';
import { formatGenres, formatProfessions, formatEmployment } from 'utils/text';
import { TRACK_EVENTS, ENTITY_CARD_CLICK } from 'utils/tracking';

import { ENTITY_TYPES } from 'operabase-router/constants';

import classes from './EntityDisplayCard.module.scss';

export const VARIANTS = {
  ROUNDED: 'rounded',
  PORTRAIT: 'portrait',
};

interface EntityDisplayCardProps {
  entity: any;
  entityType: string;
  trackingData?: any;
  variant?: 'rounded' | 'portrait';
}

function EntityDisplayCard({
  entity,
  entityType,
  trackingData,
  variant = VARIANTS.PORTRAIT as 'rounded' | 'portrait',
}: EntityDisplayCardProps) {
  const { navigate } = usePageContext() as any;
  const track = useTracking();
  const linkProps = navigate.getLinkProps({ entityType, entity, onlyLinkProps: true });

  const handleOnClick = useCallback(() => {
    track.click({
      name: TRACK_EVENTS.CLICK,
      id: ENTITY_CARD_CLICK(ENTITY_TYPES.PROFILE),
    });
  }, [track]);

  const imgTransformations = useMemo(() => {
    const keyMap = {
      [ENTITY_TYPES.PROFILE]: {
        [VARIANTS.PORTRAIT]: TRANSFORMATIONS.PROFILE_DISPLAY_CARD,
        [VARIANTS.ROUNDED]: TRANSFORMATIONS.PROFILE_THUMBNAIL,
      },
      [ENTITY_TYPES.ORGANIZATION]: {
        [VARIANTS.PORTRAIT]: TRANSFORMATIONS.ORG_DISPLAY_CARD,
        [VARIANTS.ROUNDED]: TRANSFORMATIONS.ORG_LOGO,
      },
    };

    const entityTypeConfig = keyMap[entityType];

    if (!entityTypeConfig) {
      return TRANSFORMATIONS.POSTER;
    }

    return entityTypeConfig[variant] || TRANSFORMATIONS.POSTER;
  }, [entityType, variant]);

  const imgSizes = useMemo(
    () => ({
      [VARIANTS.PORTRAIT]: {
        height: 152,
        width: 112,
      },
      [VARIANTS.ROUNDED]: {
        height: 90,
        width: 90,
      },
    }),
    [],
  );

  const fontSizes = {
    [VARIANTS.PORTRAIT]: 44,
    [VARIANTS.ROUNDED]: 22,
  };

  const imgProps = useMemo(
    () => ({
      transformations: imgTransformations,
      disableNextImage: true,
      useIntersectionObserver: true,
      alt: entity?.name,
      title: entity?.name,
      height: imgSizes[variant].height,
      width: imgSizes[variant].width,
    }),
    [entity?.name, imgSizes, imgTransformations, variant],
  );
  const src = getMediaSource(
    {
      id: entity?.id,
      file: { urls: entity?.image || entity?.logo },
    },
    true,
  );

  const { subTitle, genres, employment, professions, organizationType, typographyCount } = useMemo(() => {
    switch (entityType) {
      case ENTITY_TYPES.PROFILE: {
        const formattedEmployment = formatEmployment(entity?.organizations, 25);
        const formattedProfessions = formatProfessions(entity?.professions, 18);
        const formattedGenres = formatGenres(entity?.workTypes, 16);

        let count = 0;

        if (formattedEmployment) {
          count += 1;
        }

        if (formattedProfessions) {
          count += 1;
        }

        if (formattedGenres) {
          count += 1;
        }

        return {
          employment: formattedEmployment,
          professions: formattedProfessions,
          genres: formattedGenres,
          typographyCount: count,
        };
      }
      case ENTITY_TYPES.ORGANIZATION: {
        const formattedGenres = formatGenres(entity?.workTypes, 16);
        const location = getOrganizationLocation(entity);

        let count = 0;

        if (location) {
          count += 1;
        }

        if (entity?.organizationType?.name) {
          count += 1;
        }

        if (formattedGenres) {
          count += 1;
        }

        return {
          subTitle: location,
          genres: formattedGenres,
          organizationType: entity?.organizationType?.name,
          typographyCount: count,
        };
      }
      default: {
        return {
          typographyCount: 0,
        };
      }
    }
  }, [entity, entityType]);

  const typographyModifier = typographyCount >= 4 ? 'singleLine' : 'multiLine';

  const fallBackComponent = (
    <ImageAvatar
      type="V4"
      name={entity?.name}
      fontSize={fontSizes[variant]}
      shape="rect"
      width={imgSizes[variant].width}
      height={imgSizes[variant].height}
      className=""
    />
  );

  const content = (
    <ImageWithFallback
      src={src}
      fallBack={fallBackComponent}
      width={imgSizes[variant].width}
      height={imgSizes[variant].height}
      {...imgProps}
    />
  );

  const organisationType = entity?.organizationType?.slug;
  const completeTrackingData = {
    ...trackingData,
    entityId: entity?.id,
    entityName: entity?.name,
    entityType,
    meta: {
      ...(entity?.source_tag && { sourceTag: entity.source_tag }),
      ...(organisationType && { organisationType }),
      ...trackingData?.meta,
    },
  };

  return (
    <LinkButton
      styles={{
        root: classes.link,
      }}
      variant="native"
      title={entity?.name}
      isLink
      trackingData={completeTrackingData}
      {...linkProps}
      onClick={handleOnClick}
    >
      <ImpressionTracker data={completeTrackingData}>
        <div
          className={classnames(classes.card, {
            [classes[`card__variant_${variant}`]]: !!classes[`card__variant_${variant}`],
          })}
        >
          <div
            className={classnames(classes.card__img, {
              [classes[`card__img__variant_${variant}`]]: !!classes[`card__img__variant_${variant}`],
            })}
          >
            {content}

            {variant === VARIANTS.PORTRAIT && (
              <div className={classes.card__chevron}>
                <SpriteIcon
                  icon="chevron_right"
                  className={classes.icon}
                  size={16}
                  fill="#fff"
                  ariaLabel="Navigate to profile"
                  onClick={undefined}
                />
              </div>
            )}
          </div>
          <div
            className={classnames(classes.card__textWrapper, {
              [classes[`card__textWrapper__variant_${variant}`]]: !!classes[`card__textWrapper__variant_${variant}`],
            })}
          >
            <Typography
              weight="bold"
              className={classnames(classes.title, classes[`title--${typographyModifier}`], {
                [classes[`title__variant_${variant}`]]: !!classes[`title__variant_${variant}`],
              })}
              size="11"
              truncate
            >
              <EntityName
                name={entity?.name}
                entityType={entityType}
                entity={entity}
                trackEntityType={entityType}
                eventType="click"
                isRaw
                trackingData={completeTrackingData}
              />
            </Typography>

            {entityType === ENTITY_TYPES.PROFILE && employment && (
              <Typography
                size="10"
                className={classnames(classes.employment, classes[`employment--${typographyModifier}`])}
                truncate
              >
                {employment}
              </Typography>
            )}
            {entityType === ENTITY_TYPES.PROFILE && (
              <Typography
                size="10"
                className={classnames(classes.professions, classes[`professions--${typographyModifier}`])}
                truncate
              >
                {Array.isArray(professions) && professions.length > 0 ? (
                  <Overflow
                    data={professions}
                    maxCount={1}
                    renderItem={(item, info) => (
                      <Typography key={info.index}>
                        {info.index > 0 ? ', ' : ''}
                        {item}
                      </Typography>
                    )}
                    renderRest={items => (
                      <Typography size="10" color="link">
                        {professions.length ? ', ' : ''}+{items.length}
                      </Typography>
                    )}
                  />
                ) : (
                  professions
                )}
              </Typography>
            )}

            {entityType === ENTITY_TYPES.ORGANIZATION && subTitle && (
              <Typography
                size="10"
                className={classnames(classes.subtitle, classes[`subtitle--${typographyModifier}`])}
                truncate
              >
                {subTitle}
              </Typography>
            )}
            {entityType === ENTITY_TYPES.ORGANIZATION && organizationType && (
              <Typography
                size="10"
                className={classnames(classes.organizationType, classes[`organizationType--${typographyModifier}`])}
                truncate
              >
                {organizationType}
              </Typography>
            )}
            {[ENTITY_TYPES.PROFILE, ENTITY_TYPES.ORGANIZATION].includes(entityType) &&
              (genres?.length > 0 || genres) && (
                <Typography
                  size="9"
                  className={classnames(classes.genres, classes[`genres--${typographyModifier}`])}
                  textTransform="uppercase"
                  truncate
                >
                  {Array.isArray(genres) && genres.length > 0 ? (
                    <Overflow
                      data={genres}
                      maxCount={1}
                      renderItem={(item, info) => (
                        <Typography key={info.index}>
                          {info.index > 0 ? ', ' : ''}
                          {item}
                        </Typography>
                      )}
                      renderRest={items => (
                        <Typography size="9" color="link">
                          {genres.length ? ', ' : ''}+{items.length}
                        </Typography>
                      )}
                    />
                  ) : (
                    genres
                  )}
                </Typography>
              )}
          </div>
        </div>
      </ImpressionTracker>
    </LinkButton>
  );
}

export default EntityDisplayCard;
